'use client';

import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PublicUserProfile {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string;
  bio: string;
  role: string;
  assignedTag?: string;
  followerCount: number;
  followingCount: number;
  clipsCount: number;
}

// ─── User Search ──────────────────────────────────────────────────────────────

export async function searchUsersFromDB(query: string): Promise<PublicUserProfile[]> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('search_profiles', { search_query: query || '' });
  if (error) {
    console.log('Search profiles error:', error.message);
    return [];
  }
  return (data || []).map((row: any) => ({
    id: row.id,
    username: row.username,
    displayName: row.display_name,
    avatarUrl: row.avatar_url || '',
    bio: row.bio || '',
    role: row.role || 'viewer',
    assignedTag: row.assigned_tag || '',
    followerCount: row.follower_count || 0,
    followingCount: row.following_count || 0,
    clipsCount: row.clips_count || 0,
  }));
}

export async function getProfileByUsername(username: string): Promise<PublicUserProfile | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('username', username.toLowerCase())
    .eq('is_profile_complete', true)
    .maybeSingle();

  if (error || !data) return null;

  return {
    id: data.id,
    username: data.username,
    displayName: data.display_name,
    avatarUrl: data.avatar_url || '',
    bio: data.bio || '',
    role: data.role || 'viewer',
    assignedTag: data.assigned_tag || '',
    followerCount: data.follower_count || 0,
    followingCount: data.following_count || 0,
    clipsCount: data.clips_count || 0,
  };
}

// ─── Follow Operations ────────────────────────────────────────────────────────

export async function checkIsFollowing(followerId: string, followingId: string): Promise<boolean> {
  const supabase = createClient();
  const { data } = await supabase
    .from('follows')
    .select('id')
    .eq('follower_id', followerId)
    .eq('following_id', followingId)
    .maybeSingle();
  return !!data;
}

export async function followUser(followerId: string, followingId: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase
    .from('follows')
    .insert({ follower_id: followerId, following_id: followingId });
  return !error;
}

export async function unfollowUser(followerId: string, followingId: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase
    .from('follows')
    .delete()
    .eq('follower_id', followerId)
    .eq('following_id', followingId);
  return !error;
}

export async function toggleFollowDB(followerId: string, followingId: string): Promise<boolean> {
  const isAlreadyFollowing = await checkIsFollowing(followerId, followingId);
  if (isAlreadyFollowing) {
    await unfollowUser(followerId, followingId);
    return false;
  } else {
    await followUser(followerId, followingId);
    return true;
  }
}

// ─── Legacy localStorage shims (kept for backward compat during transition) ──

export function getFollowing(_userId: string): string[] { return []; }
export function getFollowers(_username: string): string[] { return []; }
export function getFollowerCount(_username: string): number { return 0; }
export function getFollowingCount(_userId: string): number { return 0; }
export function isFollowing(_userId: string, _username: string): boolean { return false; }
export function toggleFollow(_userId: string, _username: string): boolean { return false; }
export function onFollowsUpdated(_callback: () => void): () => void { return () => {}; }
export function getAllPublicUsers(): PublicUserProfile[] { return []; }
export function searchUsers(_query: string): PublicUserProfile[] { return []; }
